import { useCallback, useEffect, useState } from 'react';
import { UserPlus, Users } from 'lucide-react';
import api from '../../services/api';
import { useToast } from '../../contexts/ToastContext';

/**
 * Forming a team from the people who registered.
 *
 * The participant's own route makes whoever creates the team its leader, which
 * is wrong for an organiser building teams from a list. Here the organiser says
 * who leads and who is in it, and is not part of the team themselves.
 */

interface Person {
  userId: string | null;
  name: string;
  email: string;
  hasAccount: boolean;
  inTeam: string | null;
  skills: string[];
  lookingForTeam: boolean;
}

interface HackathonOption { _id: string; title: string }

export default function FormTeamPanel({ onCreated }: { onCreated?: () => void }) {
  const { success, error } = useToast();
  const [hackathons, setHackathons] = useState<HackathonOption[]>([]);
  const [hackathonId, setHackathonId] = useState('');
  const [people, setPeople] = useState<Person[]>([]);
  const [counts, setCounts] = useState({ total: 0, withoutAccount: 0, available: 0 });
  const [name, setName] = useState('');
  const [leaderId, setLeaderId] = useState('');
  const [picked, setPicked] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [releasing, setReleasing] = useState(false);

  useEffect(() => {
    api.get('/hackathons')
      .then((res) => {
        const list = Array.isArray(res.data) ? res.data : (res.data.hackathons || []);
        setHackathons(list.map((h: any) => ({ _id: h._id, title: h.title })));
      })
      .catch(() => { /* an empty list is handled below */ });
  }, []);

  const load = useCallback(async (id: string) => {
    if (!id) { setPeople([]); return; }
    try {
      const res = await api.get(`/teams/admin/available/${id}`);
      setPeople(res.data.people || []);
      setCounts({ total: res.data.total, withoutAccount: res.data.withoutAccount, available: res.data.available });
    } catch (err: any) {
      error(err.response?.data?.message || 'Could not load the participants');
    }
  }, [error]);

  useEffect(() => { load(hackathonId); setPicked([]); setLeaderId(''); }, [hackathonId, load]);

  const releaseStranded = async () => {
    setReleasing(true);
    try {
      // Ask first, so the count in the confirmation is the real one rather than
      // a promise about what might be found.
      const look = await api.post(`/teams/admin/release-stranded/${hackathonId}?dryRun=true`);
      const n = look.data.stranded;
      if (!n) {
        success('Nobody is stranded: every registration claiming a team names one that exists.');
        return;
      }
      const names = look.data.people.map((p: any) => p.name).join(', ');
      if (!window.confirm(`${n} person(s) are held by a team that no longer exists:\n\n${names}\n\nRelease them so they can join or start another team?`)) return;

      const res = await api.post(`/teams/admin/release-stranded/${hackathonId}`);
      success(res.data.message);
      load(hackathonId);
    } catch (err: any) {
      error(err.response?.data?.message || 'Could not check for stranded registrations');
    } finally {
      setReleasing(false);
    }
  };

  const toggle = (id: string) => {
    setPicked((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
    if (leaderId === id) setLeaderId('');
  };

  const create = async () => {
    if (!name.trim()) { error('Give the team a name'); return; }
    if (!leaderId) { error('Choose who leads the team'); return; }

    setBusy(true);
    try {
      const res = await api.post('/teams/admin/create', {
        hackathonId, name, leaderId,
        memberIds: picked.filter((id) => id !== leaderId)
      });
      success(`${res.data.team.name} created — join code ${res.data.team.joinCode}`);
      setName(''); setPicked([]); setLeaderId('');
      load(hackathonId);
      onCreated?.();
    } catch (err: any) {
      error(err.response?.data?.message || 'Could not create the team');
    } finally { setBusy(false); }
  };

  const free = people.filter((p) => p.hasAccount && !p.inTeam);

  return (
    <div className="space-y-5">
      <div className="rounded-lg border border-gray-200 bg-white p-5">
        <h3 className="mb-3 flex items-center gap-2 text-lg font-semibold">
          <Users className="h-5 w-5 text-gray-500" /> Form a team
        </h3>

        <label htmlFor="ft-hackathon" className="mb-1 block text-sm font-medium text-gray-700">Hackathon</label>
        <select
          id="ft-hackathon" value={hackathonId} onChange={(e) => setHackathonId(e.target.value)}
          className="mb-4 w-full max-w-md rounded-md border border-gray-300 px-3 py-2"
        >
          <option value="">Choose a hackathon…</option>
          {hackathons.map((h) => <option key={h._id} value={h._id}>{h.title}</option>)}
        </select>

        {hackathonId && (
          <>
            <div className="mb-4 rounded-md border border-gray-200 p-3">
              <p className="text-sm font-medium">Held by a team that no longer exists</p>
              <p className="mt-1 text-xs text-gray-500">
                Deleting a team used to leave its members still marked as having one, so
                they were refused every other team. Deletion releases people now; this
                lets out anyone stranded before that.
              </p>
              <button
                type="button"
                disabled={releasing}
                onClick={releaseStranded}
                className="mt-2 rounded-md border border-gray-300 px-3 py-1.5 text-sm hover:bg-gray-50 disabled:opacity-50"
              >
                {releasing ? 'Checking…' : 'Find and release them'}
              </button>
            </div>

            <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
              <div className="rounded-md border border-gray-200 p-3">
                <p className="text-xs text-gray-500">Participants</p>
                <p className="text-xl font-semibold">{counts.total}</p>
              </div>
              <div className="rounded-md border border-gray-200 p-3">
                <p className="text-xs text-gray-500">Free to be teamed</p>
                <p className="text-xl font-semibold text-green-700">{counts.available}</p>
              </div>
              <div className={`rounded-md border p-3 ${counts.withoutAccount ? 'border-amber-300 bg-amber-50' : 'border-gray-200'}`}>
                <p className="text-xs text-gray-500">No account yet</p>
                <p className="text-xl font-semibold text-amber-800">{counts.withoutAccount}</p>
              </div>
            </div>

            {counts.withoutAccount > 0 && (
              <p className="mb-4 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                <strong>{counts.withoutAccount}</strong> registered without an account and cannot be put in
                a team. Ask them to sign up with the address they registered with, and their registration
                attaches itself.
              </p>
            )}

            <label htmlFor="ft-name" className="mb-1 block text-sm font-medium text-gray-700">Team name</label>
            <input
              id="ft-name" value={name} onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Team Aakash"
              className="mb-4 w-full max-w-md rounded-md border border-gray-300 px-3 py-2"
            />

            <p className="mb-2 text-sm font-medium text-gray-700">
              Who is in it? <span className="font-normal text-gray-500">Tick the members, then choose one to lead.</span>
            </p>

            {free.length === 0 ? (
              <p className="rounded-md bg-gray-50 p-4 text-sm text-gray-600">
                Nobody is available. Either everyone is already in a team, or nobody registered has an account yet.
              </p>
            ) : (
              <ul className="max-h-80 space-y-1 overflow-y-auto rounded-md border border-gray-200 p-2">
                {free.map((p) => (
                  <li key={p.userId!} className="flex items-center gap-3 rounded px-2 py-2 hover:bg-gray-50">
                    <input
                      type="checkbox" checked={picked.includes(p.userId!)}
                      onChange={() => toggle(p.userId!)} className="h-4 w-4"
                    />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-gray-900">{p.name || p.email}</p>
                      <p className="truncate text-xs text-gray-500">
                        {p.email}
                        {p.skills.length > 0 && <> · {p.skills.slice(0, 4).join(', ')}</>}
                        {!p.lookingForTeam && <> · <span className="text-amber-700">said they have a team</span></>}
                      </p>
                    </div>
                    {picked.includes(p.userId!) && (
                      <label className="flex shrink-0 cursor-pointer items-center gap-1.5 text-xs text-gray-700">
                        <input
                          type="radio" name="ft-leader" checked={leaderId === p.userId}
                          onChange={() => setLeaderId(p.userId!)} className="h-3.5 w-3.5"
                        />
                        leader
                      </label>
                    )}
                  </li>
                ))}
              </ul>
            )}

            <button
              type="button" onClick={create} disabled={busy || !name.trim() || !leaderId}
              className="mt-4 inline-flex items-center gap-2 rounded-md bg-blue-600 px-5 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
            >
              <UserPlus className="h-4 w-4" />
              {busy ? 'Creating…' : `Create the team${picked.length ? ` with ${picked.length}` : ''}`}
            </button>
            {!leaderId && picked.length > 0 && (
              <p className="mt-2 text-sm text-amber-800">Choose which of them leads the team.</p>
            )}
          </>
        )}
      </div>
    </div>
  );
}
